import React from 'react';
import { Text } from '@cuemath/web-comps';
import COLORS from '../../constants/colors';
import { Container, Grid, GridColumn, Header, Loader, Segment } from 'semantic-ui-react';
import LeadDetails from './lead-details';
import Actions from './actions';
import {
  useGetProspectDetails,
  usePostLeadDetails,
  invalidateProspectDetails,
  usePostConfirmationLink,
  usePostAssessment,
  usePostTrialCancel,
  usePostTrialSlot,
} from 'actions/demo';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { MODAL_NAMES } from './utils';
import CancelTrial from './cancel-trial';
import ModifyEcna from './modify-enca';
import Ecna from './ecna';
import { DateTime } from 'luxon';
import AssignSlot from './assign-slot';
import { Separator } from '@cuemath/comps';
import { STATUS_TYPES } from './button-config';

const Overlay = styled.div`
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(6px);
  z-index: 10;
`;

const AdmissionTool = () => {
  const [modalName, setModalName] = React.useState('');
  const [editData, setEditData] = React.useState({
    canEditData: false,
    editedFields: [],
    editedValues: {},
  });
  const [progressStates, setProgressStates] = React.useState({
    loading: false,
    error: false,
    success: false,
    errorMessage: '',
  });
  const [otpState, setOtpState] = React.useState(null);
  const timeoutRef = React.useRef(null);
  const { prospectId } = useParams();

  const {
    get: getProspectDetails,
    data: prospectData,
    isStale: isProspectDataStale,
    isProcessing: isProspectDataProcessing,
    isProcessed: isProspectDataProcessed,
  } = useGetProspectDetails();

  const {
    cna_data: cnaData,
    parent_user_id: parentUserId,
    student_id: studentId,
    email,
    grade,
    timezone,
    parent_name: parentName,
    student_name: studentName,
    demo_time: demoTime,
    region,
    status,
    teacher_id: teacherId,
    confirmation_time: confirmationTime,
    student_direct_transfer_id: studentDirectTransferId,
    available_timezones: availableTimezones = [],
    opportunity_region: opportunityRegion,
  } = prospectData || {};

  const setLoadingState = React.useCallback(() => {
    setProgressStates({
      loading: true,
      error: false,
      success: false,
      errorMessage: '',
    });
  }, []);

  const setSuccessState = React.useCallback(() => {
    setProgressStates({
      loading: false,
      error: false,
      success: true,
      errorMessage: '',
    });
  }, []);

  const setErrorState = React.useCallback(errorMessage => {
    setProgressStates({
      loading: false,
      error: true,
      success: false,
      errorMessage,
    });
  }, []);

  const resetProgressStates = React.useCallback(() => {
    setProgressStates({
      loading: false,
      error: false,
      success: false,
      errorMessage: '',
    });
  }, []);

  const onComplete = React.useCallback(
    (error, action) => {
      if (error) {
        if (action === 'edit-lead-details') {
          setEditData({
            canEditData: false,
            editedFields: [],
            editedValues: {
              grade,
              timezone,
              parent_name: parentName,
              student_name: studentName,
              email,
            },
          });
        }
        setErrorState(error);
        timeoutRef.current = setTimeout(() => {
          resetProgressStates();
        }, 1500);
      } else {
        if (action === 'edit-lead-details') {
          setEditData({
            canEditData: false,
            editedFields: [],
            editedValues: {},
          });
        }
        setSuccessState();
        invalidateProspectDetails(prospectId);
      }
    },
    [
      email,
      grade,
      parentName,
      prospectId,
      resetProgressStates,
      setErrorState,
      setSuccessState,
      studentName,
      timezone,
    ],
  );

  const { post: postLeadDetails } = usePostLeadDetails({
    onComplete: error => onComplete(error, 'edit-lead-details'),
  });

  const { post: postConfirmationLink } = usePostConfirmationLink({
    onComplete,
  });

  const { post: postAssessment } = usePostAssessment({
    onComplete,
  });

  const { post: postTrialCancel } = usePostTrialCancel({
    onComplete,
  });

  const { post: postTrialSlot } = usePostTrialSlot({
    onComplete: error => {
      if (error) {
        if (otpState?.state) {
          setOtpState(prev => ({ ...prev, error, isLoading: false, isDisabled: false }));
        } else {
          setErrorState(error);
          timeoutRef.current = setTimeout(() => {
            resetProgressStates();
          }, 1500);
        }
      } else {
        if (MODAL_NAMES.rescheduleSlotSameTutor && otpState?.state === 'initialize') {
          setOtpState({ state: 'sent', isLoading: false });
        } else if (MODAL_NAMES.rescheduleSlotSameTutor && otpState?.state === 'sent') {
          setOtpState(null);
          closeModal();
          invalidateProspectDetails(prospectId);
        } else {
          setSuccessState();
          invalidateProspectDetails(prospectId);
        }
      }
    },
  });

  React.useEffect(() => {
    getProspectDetails(prospectId);
  }, [getProspectDetails, prospectId]);

  React.useEffect(() => {
    if (isProspectDataStale) {
      getProspectDetails(prospectId);
    }
  }, [getProspectDetails, isProspectDataStale, prospectId]);

  React.useEffect(() => {
    if (isProspectDataProcessed) {
      setEditData(prev => ({
        ...prev,
        editedValues: {
          grade,
          timezone,
          parent_name: parentName,
          student_name: studentName,
          email,
        },
      }));
      resetProgressStates();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [
    email,
    grade,
    isProspectDataProcessed,
    parentName,
    resetProgressStates,
    studentName,
    timezone,
  ]);

  const onEditDetails = React.useCallback(() => {
    setEditData(prev => ({
      canEditData: true,
      editedFields: ['email', 'grade', 'timezone', 'parent_name', 'student_name'],
      editedValues: { ...prev.editedValues },
    }));
  }, []);

  const onSendConfirmationEmail = React.useCallback(() => {
    setLoadingState();
    postConfirmationLink({ parent_user_id: parentUserId }, studentId);
  }, [setLoadingState, postConfirmationLink, parentUserId, studentId]);

  const onUpdateLeadDetails = React.useCallback(() => {
    setLoadingState();
    const updatedData = {
      email: editData.editedValues.email,
      grade: editData.editedValues.grade,
      timezone: editData.editedValues.timezone,
      parent_name: editData.editedValues.parent_name,
      student_name: editData.editedValues.student_name,
      student_id: studentId,
    };
    postLeadDetails(updatedData, parentUserId);
  }, [
    setLoadingState,
    editData.editedValues.email,
    editData.editedValues.grade,
    editData.editedValues.timezone,
    editData.editedValues.parent_name,
    editData.editedValues.student_name,
    studentId,
    postLeadDetails,
    parentUserId,
  ]);

  const saveEcna = React.useCallback(
    data => {
      setLoadingState();
      const updatedData = {
        parent_user_id: parentUserId,
        bulk_assessment_data_list: Object.entries(data).map(([key, value]) => ({
          question_type: key,
          answer: value,
        })),
      };
      postAssessment(updatedData, studentId);
    },
    [setLoadingState, parentUserId, postAssessment, studentId],
  );

  const onCancelTrial = React.useCallback(
    data => {
      const { cancellationReason, customReason } = data || {};
      setLoadingState();
      const updatedData =
        cancellationReason === 'Other'
          ? { cancellation_reason: customReason.replace(/\s+/g, ' ').trim() }
          : { cancellation_reason: cancellationReason };
      postTrialCancel(updatedData, studentId);
    },
    [postTrialCancel, setLoadingState, studentId],
  );

  const onAssignSlots = React.useCallback(
    (data, action) => {
      const { time, followUpDate, verificationCode, timezone: selectedTimezone } =
        data || {};
      const { from, to } = time || {};
      if (action !== 'send_confirmation_code' && action !== 'verify_confirmation_code') {
        setLoadingState();
      }
      const updatedData = {
        action,
        parent_user_id: parentUserId,
        current_stage: status,
        previous_confirmation_time: confirmationTime,
        teacher_id: teacherId,
        verification_code: verificationCode,
        student_direct_transfer_id: studentDirectTransferId,
        trial_slot: {
          from,
          to,
          timezone: selectedTimezone || timezone,
        },
      };
      if (followUpDate) {
        const followUpDt = DateTime.fromISO(followUpDate, { zone: 'utc' });
        const followUpEpoch = Math.floor(followUpDt.toSeconds());
        updatedData.trial_slot.from = followUpEpoch;
      }
      postTrialSlot(updatedData, studentId);
    },
    [
      confirmationTime,
      parentUserId,
      postTrialSlot,
      setLoadingState,
      status,
      studentId,
      teacherId,
      timezone,
    ],
  );

  const openModal = React.useCallback(name => {
    setModalName(name);
  }, []);

  const closeModal = React.useCallback(() => {
    setModalName('');
  }, []);

  if (
    isProspectDataProcessing &&
    !(progressStates.loading || progressStates.success || progressStates.error)
  ) {
    return (
      <Grid textAlign="center" verticalAlign="middle" style={{ minHeight: '100vh' }}>
        <GridColumn>
          <Loader active size="large">
            Loading...
          </Loader>
        </GridColumn>
      </Grid>
    );
  }

  return (
    <>
      {(progressStates.loading || progressStates.success || progressStates.error) && (
        <Overlay>
          {progressStates.success && (
            <Text variant="eyebrow1" color={COLORS.WHITE}>
              Completed ✅
            </Text>
          )}
          {progressStates.error && (
            <Text variant="eyebrow1" color={COLORS.WHITE}>
              {progressStates.errorMessage || 'Something went wrong'} ❌
            </Text>
          )}
          {progressStates.loading && (
            <Loader active size="large" inverted>
              Loading
            </Loader>
          )}
        </Overlay>
      )}
      <Container>
        <Separator height={1} transparent />
        <Header as="h1" textAlign="center">
          Cuemath Admissions Tool
        </Header>
        <Header as="h3" textAlign="center" color="grey">
          Assign slots, reschedule, or cancel trials for all leads
        </Header>
        <Grid columns={2}>
          <Grid.Column width={10}>
            <Segment>
              <LeadDetails
                prospectData={prospectData}
                editData={editData}
                setEditData={setEditData}
                onUpdateLeadDetails={onUpdateLeadDetails}
              />
            </Segment>
            <Ecna cnaData={cnaData} />
          </Grid.Column>
          <Grid.Column width={6}>
            <Actions
              onEditDetails={onEditDetails}
              editData={editData}
              onSendConfirmationEmail={onSendConfirmationEmail}
              openModal={openModal}
              status={status}
              timezone={timezone}
              studentName={studentName}
              grade={grade}
              cnaData={cnaData}
              confirmedOn={confirmationTime}
            />
          </Grid.Column>
        </Grid>
        {modalName === MODAL_NAMES.addCustomerNeeds && (
          <ModifyEcna
            closeModal={closeModal}
            saveEcna={saveEcna}
            cnaData={cnaData}
            studentName={studentName}
            region={region}
            timezone={timezone}
            grade={grade}
          />
        )}
        {modalName === MODAL_NAMES.editCustomerNeeds && (
          <ModifyEcna
            closeModal={closeModal}
            saveEcna={saveEcna}
            cnaData={cnaData}
            isEditable
            studentName={studentName}
            region={region}
            timezone={timezone}
            grade={grade}
          />
        )}
        {modalName === MODAL_NAMES.cancelTrial && (
          <CancelTrial
            closeModal={closeModal}
            studentName={studentName}
            onCancel={onCancelTrial}
            demoTime={demoTime}
            timezone={timezone}
            onAssignSlots={onAssignSlots}
          />
        )}
        {modalName === MODAL_NAMES.assignSlot && (
          <AssignSlot
            closeModal={closeModal}
            studentName={studentName}
            timezone={timezone}
            opportunityRegion={opportunityRegion}
            onAssignSlots={onAssignSlots}
            bufferHours={status === STATUS_TYPES.TRANSFER_REQUESTED ? 48 : 72}
            type={MODAL_NAMES.assignSlot}
            availableTimezones={availableTimezones}
          />
        )}
        {modalName === MODAL_NAMES.rescheduleSlot && (
          <AssignSlot
            closeModal={closeModal}
            studentName={studentName}
            timezone={timezone}
            opportunityRegion={opportunityRegion}
            onAssignSlots={onAssignSlots}
            bufferHours={72}
            type={MODAL_NAMES.rescheduleSlot}
            availableTimezones={availableTimezones}
          />
        )}
        {modalName === MODAL_NAMES.rescheduleSlotDifferentTutor && (
          <AssignSlot
            closeModal={closeModal}
            studentName={studentName}
            timezone={timezone}
            opportunityRegion={opportunityRegion}
            onAssignSlots={onAssignSlots}
            bufferHours={48}
            type={MODAL_NAMES.rescheduleSlotDifferentTutor}
            availableTimezones={availableTimezones}
          />
        )}
        {modalName === MODAL_NAMES.rescheduleSlotSameTutor && (
          <AssignSlot
            closeModal={closeModal}
            studentName={studentName}
            timezone={timezone}
            opportunityRegion={opportunityRegion}
            onAssignSlots={onAssignSlots}
            bufferHours={0.5}
            type={MODAL_NAMES.rescheduleSlotSameTutor}
            setOtpState={setOtpState}
            otpState={otpState}
            progressStates={progressStates}
            availableTimezones={availableTimezones}
          />
        )}
      </Container>
    </>
  );
};

export default AdmissionTool;
